import { Add } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from '@mui/material';
import _ from 'lodash';
import React, { useContext, useState } from 'react';
import { DisableContext } from '../../../contexts/DisableContextProvider';
import { ROUTE_PATHS } from '../../../routes/Routes';
import { listPageTableRowStyling } from '../../../styles/sxStyling';
import { Contact } from '../../../types/Contact';
import { EmailTemplateLink, EmailTemplateStartCondition } from '../../../types/EmailTemplate';
import { UserSettingsCollapseKeys } from '../../../types/User';
import ResponsiveButton from '../../../uikit/buttons/ResponsiveButton';
import { addOrRemoveDaysToDate } from '../../../utility/dateUtils';
import AccordionSection from '../../AccordionSection';
import { DependencyOption } from '../../tasks/StartCondition';
import AppLink from '../AppLink/AppLink';
import RolesPopover from '../popover/RolesPopover';
import AddEmailTemplateModal, { ModalAddLinkData } from './AddEmailTemplateModal';
import EmailDataModal from './EmailDataModal/EmailDataModal';
import EmailPreviewModal from './EmailPreviewModal/EmailPreviewModal';

export enum EmailTemplateSectionModule {
  EVENT = 'event',
  EVENT_TEMPLATE = 'event_template',
}
interface IEmailTemplateSectionProps {
  moduleType: EmailTemplateSectionModule;
  linkingModels: EmailTemplateLink[];
  setLinkingModels: (linkingModels: EmailTemplateLink[]) => void;
  collapseId: UserSettingsCollapseKeys;
  startDate?: string;
  availableCustomFields?: string[];
  eventContacts?: Contact[];
  dependencyOptions?: DependencyOption[];
}

export default function EmailTemplatesSection({
  moduleType,
  linkingModels,
  setLinkingModels,
  collapseId,
  startDate,
  availableCustomFields = [],
  eventContacts = [],
  dependencyOptions = [],
}: IEmailTemplateSectionProps) {
  const { disabled } = useContext(DisableContext);

  const [isEmailTemplateModalOpen, setEmailTemplateModalOpen] = useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplateLink | null>(null);
  const [emailPreviewModalOpen, setEmailPreviewModalOpen] = useState<boolean>(false);
  const [emailDataModalOpen, setEmailDataModalOpen] = useState<boolean>(false);

  const handleAddEmailTemplate: ModalAddLinkData = (linkingData) => {
    if (selectedTemplate) {
      setLinkingModels(
        _.map(linkingModels, (template) => (template.uuid === selectedTemplate.uuid ? { ...linkingData, new_record: template.new_record } : template))
      );
    } else {
      setLinkingModels([...linkingModels, { ...linkingData, new_record: true }]);
    }

    setEmailTemplateModalOpen(false);
    setSelectedTemplate(null);
  };

  const handleRemoveEmailTemplate = (uuid: string) => {
    setLinkingModels(_.reject(linkingModels, { uuid }));
  };

  const handleEditEmailTemplate = (template: EmailTemplateLink) => {
    setSelectedTemplate(template);
    setEmailTemplateModalOpen(true);
  };

  const handlePreviewEmailTemplate = (template: EmailTemplateLink) => {
    setSelectedTemplate(template);
    setEmailPreviewModalOpen(true);
  };

  const handleOpenDataModal = (linkingModel: EmailTemplateLink) => {
    setSelectedTemplate(linkingModel);
    setEmailDataModalOpen(true);
  };

  const title = moduleType === EmailTemplateSectionModule.EVENT_TEMPLATE ? 'Email Templates' : 'Emails';
  const nameContent = (linkingModel: EmailTemplateLink) => {
    if (moduleType === EmailTemplateSectionModule.EVENT_TEMPLATE) {
      return <AppLink to={`/${ROUTE_PATHS.EMAIL_TEMPLATES}/${linkingModel.email_template_uuid}`}>{linkingModel.email_template_name}</AppLink>;
    }

    return linkingModel.new_record ? (
      <span>{linkingModel.email_template_name}</span>
    ) : (
      <AppLink to="#" onClick={() => handleOpenDataModal(linkingModel)}>
        {linkingModel.email_template_name}
      </AppLink>
    );
  };

  // Updated send condition rendering
  // Consolidated send condition + date/offset logic
  const renderSendConditionDisplay = (linkingModel: EmailTemplateLink) => {
    const { start_condition, offset } = linkingModel;

    switch (start_condition) {
      case EmailTemplateStartCondition.MANUAL:
        return 'Manual';
      case EmailTemplateStartCondition.DATE_OFFSET: {
        return `Offset (Days): ${addOrRemoveDaysToDate({ date: startDate, days: offset ?? 0 })}`;
      }
      case EmailTemplateStartCondition.TASK_DEPENDENCY:
        return 'On Task Completion';
      case EmailTemplateStartCondition.EVENT_DEPENDENCY:
        return 'On Event Completion';
      default:
        return '-';
    }
  };

  const emailTemplateTableHeaderActions = (
    <ResponsiveButton
      startIcon={<Add />}
      variant="outlined"
      disabled={disabled}
      onClick={() => {
        setSelectedTemplate(null);
        setEmailTemplateModalOpen(true);
      }}
    >
      Add
    </ResponsiveButton>
  );

  const emailTemplateTableRows = _.map(linkingModels, (linkingModel: EmailTemplateLink) => {
    const hasInvalidFields =
      linkingModel.email_template_custom_fields && !_.every(linkingModel.email_template_custom_fields, (field) => _.includes(availableCustomFields, field));

    return (
      <TableRow key={linkingModel.uuid} sx={{ ...listPageTableRowStyling }}>
        <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {hasInvalidFields && (
            <Tooltip title="Template uses unavailable custom fields" arrow enterTouchDelay={0} leaveTouchDelay={3000} style={{ cursor: 'pointer' }}>
              <WarningAmberIcon color="warning" />
            </Tooltip>
          )}
          {nameContent(linkingModel)}
        </TableCell>
        <TableCell>{renderSendConditionDisplay(linkingModel)}</TableCell>
        <TableCell>
          <RolesPopover roles={linkingModel.roles_values} />
        </TableCell>
        <TableCell>{linkingModel.email_template_subject}</TableCell>
        <TableCell sx={{ textAlign: 'left', display: 'flex', alignItems: 'center', gap: 1 }}>
          {moduleType === EmailTemplateSectionModule.EVENT && (
            <IconButton onClick={() => handlePreviewEmailTemplate(linkingModel)}>
              <VisibilityIcon />
            </IconButton>
          )}
          {!disabled && (
            <>
              <IconButton onClick={() => handleEditEmailTemplate(linkingModel)}>
                <EditIcon />
              </IconButton>
              <IconButton onClick={() => handleRemoveEmailTemplate(linkingModel.uuid)}>
                <DeleteIcon />
              </IconButton>
            </>
          )}
        </TableCell>
      </TableRow>
    );
  });

  return (
    <>
      <AccordionSection title={title} headerElements={emailTemplateTableHeaderActions} collapseId={collapseId}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Email Template Name</TableCell>
                <TableCell>Send Condition</TableCell>
                <TableCell>Recipient Role</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{emailTemplateTableRows}</TableBody>
          </Table>
        </TableContainer>
      </AccordionSection>

      <AddEmailTemplateModal
        isOpen={isEmailTemplateModalOpen}
        onCancel={() => {
          setEmailTemplateModalOpen(false);
          setSelectedTemplate(null);
        }}
        onAdd={handleAddEmailTemplate}
        emailTemplateLink={selectedTemplate}
        dependencyOptions={dependencyOptions}
      />

      {selectedTemplate && moduleType === EmailTemplateSectionModule.EVENT && (
        <EmailPreviewModal
          selectedTemplate={selectedTemplate}
          eventContacts={eventContacts}
          open={emailPreviewModalOpen}
          onClose={() => {
            setEmailPreviewModalOpen(false);
            setSelectedTemplate(null);
          }}
        />
      )}

      <EmailDataModal
        selectedTemplate={selectedTemplate}
        open={emailDataModalOpen}
        onClose={() => {
          setEmailDataModalOpen(false);
          setSelectedTemplate(null);
        }}
      />
    </>
  );
}
